class PickupAvailability extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
  }

  connectedCallback() {
    this.productInfo = this.closest('product-info');
    if (this.productInfo) {
      this.boundOnVariantChange = this.onVariantChange.bind(this);
      this.productInfo.addEventListener('variant:changed', this.boundOnVariantChange);
    }
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.productInfo && this.boundOnVariantChange) {
      this.productInfo.removeEventListener('variant:changed', this.boundOnVariantChange);
    }
  }

  onVariantChange(event) {
    const variantId = event.detail.variant?.id;
    this.fetchAvailability(variantId);
  }

  async fetchAvailability(variantId) {
    if (!variantId) {
      this.innerHTML = '';
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    
    // Clear old state to prevent stale data visibility
    this.innerHTML = '';

    const rootUrl = this.dataset.rootUrl || '/';
    const base = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
    const url = `${base}variants/${variantId}?section_id=pickup-availability`;

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      const text = await response.text();
      
      const parser = new DOMParser();
      const html = parser.parseFromString(text, 'text/html');
      const pickupContent = html.querySelector('.shopify-section');
      
      if (pickupContent) {
        this.innerHTML = pickupContent.innerHTML;
      } else {
        this.innerHTML = '';
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch pickup availability', error);
      }
    }
  }
}

if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', PickupAvailability);
}

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.onMinusClick = this.onMinusClick.bind(this);
    this.onPlusClick = this.onPlusClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.minusButton = this.querySelector('button[name="minus"]');
    this.plusButton = this.querySelector('button[name="plus"]');

    if (this.minusButton) this.minusButton.addEventListener('click', this.onMinusClick);
    if (this.plusButton) this.plusButton.addEventListener('click', this.onPlusClick);
    if (this.input) {
      this.input.addEventListener('change', this.onInputChange);
      this.updateButtonStates();
      
      this.observer = new MutationObserver(() => this.updateButtonStates());
      this.observer.observe(this.input, { attributes: true, attributeFilter: ['min', 'max', 'step'] });
    }
  }

  disconnectedCallback() {
    if (this.minusButton) this.minusButton.removeEventListener('click', this.onMinusClick);
    if (this.plusButton) this.plusButton.removeEventListener('click', this.onPlusClick);
    if (this.input) this.input.removeEventListener('change', this.onInputChange);
    if (this.observer) this.observer.disconnect();
  }

  onMinusClick(event) {
    event.preventDefault();
    if (!this.input) return;
    const value = parseInt(this.input.value) || 0;
    const step = parseInt(this.input.step) || 1;
    const min = this.input.min ? parseInt(this.input.min) : 1;
    
    let newValue = value - step;
    if (newValue < min) newValue = min;
    
    this.input.value = newValue;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateButtonStates();
  }

  onPlusClick(event) {
    event.preventDefault();
    if (!this.input) return;
    const value = parseInt(this.input.value) || 0;
    const step = parseInt(this.input.step) || 1;
    const max = this.input.hasAttribute('max') ? parseInt(this.input.max) : null;
    
    let newValue = value + step;
    if (max !== null && newValue > max) newValue = max;
    
    this.input.value = newValue;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateButtonStates();
  }

  onInputChange() {
    this.validateInput();
    this.updateButtonStates();
  }

  validateInput() {
    if (!this.input) return;
    let value = parseInt(this.input.value);
    const min = parseInt(this.input.min) || 1;
    if (isNaN(value)) value = min;
    
    const step = parseInt(this.input.step) || 1;
    const max = this.input.hasAttribute('max') ? parseInt(this.input.max) : null;

    if (value < min) {
      value = min;
    } else if (max !== null && value > max) {
      value = max;
    } else {
      const stepDiff = (value - min) % step;
      if (stepDiff !== 0) {
        value = value - stepDiff;
      }
    }
    
    this.input.value = value;
  }

  updateButtonStates() {
    if (!this.input) return;
    const value = parseInt(this.input.value) || 0;
    const min = parseInt(this.input.min) || 1;
    const max = this.input.hasAttribute('max') ? parseInt(this.input.max) : null;

    if (this.minusButton) {
      this.minusButton.disabled = value <= min;
    }

    if (this.plusButton) {
      this.plusButton.disabled = max !== null && value >= max;
    }
  }
}

if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelector);
}

class ProductInfo extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    // Attempt to parse product data
    const productJson = this.querySelector('[data-product-json]');
    if (productJson) {
      try {
        this.variants = JSON.parse(productJson.textContent);
      } catch (e) {
        console.error('Failed to parse product JSON', e);
        this.variants = [];
      }
    }
    
    // Parse localized strings
    const stringsJson = this.querySelector('[data-product-strings]');
    if (stringsJson) {
      try {
        this.strings = JSON.parse(stringsJson.textContent);
      } catch (e) {
        this.strings = {};
      }
    }

    this.form = this.querySelector(`#product-form-${this.sectionId}`);
    this.variantInput = this.form ? this.form.querySelector('[name="id"]') : null;
    this.submitButton = this.form ? this.form.querySelector('button[type="submit"]') : null;
    this.submitButtonText = this.submitButton ? this.submitButton.querySelector('.button-text') || this.submitButton : null;
    
    // Variant Pickers
    this.optionsContainer = this.querySelector('.variant-picker');
    this.optionValues = Array.from(this.querySelectorAll('.variant-picker__option-value input[type="radio"]'));
    
    // Bind events
    if (this.optionsContainer) {
      this.optionsContainer.addEventListener('change', this.onVariantChange);
    }
  }

  disconnectedCallback() {
    if (this.optionsContainer) {
      this.optionsContainer.removeEventListener('change', this.onVariantChange);
    }
  }

  onVariantChange(event) {
    this.updateSelectedOptionValues();
    const currentVariant = this.resolveVariant();
    this.updateVariantInput(currentVariant);
    this.updateOptionAvailability();
    this.updateProductInformation(currentVariant);
    this.synchronizeGallery(currentVariant);
    this.synchronizeURL(currentVariant);
    
    this.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: {
        variant: currentVariant
      }
    }));
  }

  updateSelectedOptionValues() {
    // Update displayed option names (e.g. Color: Blue)
    const selectedOptions = this.getSelectedOptions();
    selectedOptions.forEach((value, index) => {
      // Find the corresponding label display
      const displaySpan = this.querySelector(`.variant-picker__option:nth-child(${index + 1}) .variant-picker__option-selected-value`);
      if (displaySpan) {
        displaySpan.textContent = ` ${value}`;
      }
    });
  }

  getSelectedOptions() {
    // Collect all selected radio buttons in order
    const fieldsets = Array.from(this.querySelectorAll('.variant-picker__option'));
    return fieldsets.map(fieldset => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  resolveVariant() {
    const selectedOptions = this.getSelectedOptions();
    if (selectedOptions.includes(null)) return null;

    return this.variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    }) || null;
  }

  updateVariantInput(variant) {
    if (!this.variantInput) return;
    
    if (variant) {
      this.variantInput.value = variant.id;
      this.variantInput.disabled = false;
    } else {
      this.variantInput.value = '';
      this.variantInput.disabled = true;
    }

    // The Payment terms block hosts Shopify's installments banner in a second product form; Shopify reads that
    // form's id input, which has to follow the selection (Phase 4A, D3).
    const installmentInput = this.querySelector(`#product-form-installment-${this.sectionId} [name="id"]`);
    if (installmentInput && variant) {
      installmentInput.value = variant.id;
      installmentInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateOptionAvailability() {
    const selectedOptions = this.getSelectedOptions();
    
    this.optionValues.forEach(input => {
      const fieldset = input.closest('.variant-picker__option');
      const fieldsets = Array.from(this.querySelectorAll('.variant-picker__option'));
      const optionIndex = fieldsets.indexOf(fieldset);
      
      if (optionIndex === -1) return;
      
      const candidateOptions = [...selectedOptions];
      candidateOptions[optionIndex] = input.value;
      
      const matchingVariant = this.variants.find(variant => {
        return variant.options.every((opt, idx) => {
           return opt === candidateOptions[idx] || candidateOptions[idx] === null;
        });
      });
      
      const label = this.querySelector(`label[for="${input.id}"]`);
      if (!label) return;

      if (!matchingVariant) {
        input.dataset.availability = 'impossible';
        label.dataset.availability = 'impossible';
      } else if (!matchingVariant.available) {
        input.dataset.availability = 'sold-out';
        label.dataset.availability = 'sold-out';
      } else {
        input.dataset.availability = 'available';
        label.dataset.availability = 'available';
      }
    });
  }

  updateProductInformation(variant) {
    this.updateSubmitButton(variant);
    this.updatePrice(variant);
    this.updateSKU(variant);
    this.updateInventory(variant);
    this.updateQuantityRules(variant);
    this.updateBadges(variant);
  }

  updateBadges(variant) {
    // Basic implementation to prevent TypeError and handle standard dynamic badges
    const badgesContainer = this.querySelector(`#badges-${this.sectionId}`);
    if (!badgesContainer) return;
    
    // We only toggle visibility of existing badges if possible, to avoid wiping out custom Liquid badges
    const soldOutBadge = badgesContainer.querySelector('.badge--sold-out');
    const saleBadge = badgesContainer.querySelector('.badge--sale');
    
    if (!variant) return;
    
    if (soldOutBadge) {
      soldOutBadge.style.display = variant.available ? 'none' : '';
    }
    
    if (saleBadge) {
      const isOnSale = variant.compare_at_price > variant.price;
      saleBadge.style.display = (isOnSale && variant.available) ? '' : 'none';
      
      // We don't dynamically recalculate percentage here to keep it simple and match existing theme approach,
      // but we ensure it shows/hides correctly.
    }
  }

  updateQuantityRules(variant) {
    const quantityInput = this.querySelector('input[name="quantity"]');
    if (!quantityInput) return;
    if (!variant || !variant.quantity_rule) return;
    
    const rule = variant.quantity_rule;
    
    quantityInput.min = rule.min || 1;
    if (rule.max) {
      quantityInput.max = rule.max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = rule.increment || 1;
    
    let value = parseInt(quantityInput.value) || rule.min;
    if (value < rule.min) {
      value = rule.min;
    } else if (rule.max && value > rule.max) {
      value = rule.max;
    }
    // ensure step alignment
    if (rule.increment > 1) {
      const stepDiff = (value - rule.min) % rule.increment;
      if (stepDiff !== 0) {
        value = value - stepDiff;
        if (value < rule.min) value += rule.increment;
      }
    }
    quantityInput.value = value;
    const qs = this.querySelector('quantity-selector');
    if (qs && typeof qs.updateButtonStates === 'function') {
      qs.updateButtonStates();
    }
  }

  updateSubmitButton(variant) {
    if (!this.submitButtonText || !this.submitButton) return;
    
    const paymentButton = this.querySelector('.shopify-payment-button');

    if (!variant) {
      this.submitButton.disabled = true;
      this.submitButtonText.textContent = this.strings.unavailable || 'Unavailable';
      if (paymentButton) paymentButton.style.display = 'none';
    } else if (!variant.available) {
      this.submitButton.disabled = true;
      this.submitButtonText.textContent = this.strings.soldOut || 'Sold out';
      if (paymentButton) paymentButton.style.display = 'none';
    } else {
      this.submitButton.disabled = false;
      this.submitButtonText.textContent = this.strings.addToCart || 'Add to cart';
      if (paymentButton) paymentButton.style.display = '';
    }
  }

  formatMoney(cents) {
    if (cents == null) return '';
    let value = (cents / 100.0).toFixed(2);
    
    const formatStr = this.dataset.moneyFormat || '${{amount}}';
    return formatStr.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  updatePrice(variant) {
    const priceBlock = this.querySelector(`#price-${this.sectionId}`);
    if (!priceBlock || !variant) return;

    const isSale = variant.compare_at_price > variant.price;
    // Prefer the strings Shopify formatted in Liquid (product-variant-data, Phase 4A, D2); formatMoney() remains
    // only for variant JSON that predates them (Quick Buy).
    const priceFormatted = variant.price_formatted || this.formatMoney(variant.price);
    const compareFormatted = variant.compare_at_price_formatted || this.formatMoney(variant.compare_at_price);
    
    let html = '<div class="price-list">';
    if (isSale) {
      html += `
        <span class="price-list__sale">
          <span class="visually-hidden">${this.strings.salePrice || 'Sale price'}</span>
          ${priceFormatted}
        </span>
        <span class="price-list__compare">
          <span class="visually-hidden">${this.strings.regularPrice || 'Regular price'}</span>
          <s class="text-subdued line-through">${compareFormatted}</s>
        </span>
      `;
    } else {
      html += `
        <span class="price-list__regular">
          <span class="visually-hidden">${this.strings.regularPrice || 'Regular price'}</span>
          ${priceFormatted}
        </span>
      `;
    }
    if (variant.unit_price_formatted) {
      html += `
        <span class="price-list__unit text-subdued text-sm block">
          <span class="visually-hidden">${this.strings.unitPrice || 'Unit price'}</span>
          ${variant.unit_price_formatted}
        </span>
      `;
    }
    html += '</div>';
    
    priceBlock.innerHTML = html;
  }

  updateSKU(variant) {
    const skuBlock = this.querySelector(`#sku-${this.sectionId}`);
    if (!skuBlock) return;
    
    if (variant && variant.sku) {
      skuBlock.innerHTML = `<span class="main-product__sku">${this.strings.sku || 'SKU'} ${variant.sku}</span>`;
    } else {
      skuBlock.innerHTML = '';
    }
  }

  updateInventory(variant) {
    const invBlock = this.querySelector(`#inventory-${this.sectionId}`);
    if (!invBlock) return;
    
    if (!variant) {
       invBlock.innerHTML = '';
       return;
    }
    
    let text = '';
    if (variant.available) {
      if (variant.low_stock) {
        text = this.strings.lowStock || 'Low stock';
      } else {
        text = this.strings.inStock || 'In stock';
      }
    } else {
      text = this.strings.outOfStock || 'Out of stock';
    }
    
    invBlock.innerHTML = `<span class="main-product__inventory">${text}</span>`;
  }

  synchronizeGallery(variant) {
    if (!variant || !variant.featured_media || !variant.featured_media.id) return;
    
    const gallery = this.closest('.shopify-section')?.querySelector('product-gallery');
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(variant.featured_media.id.toString());
    }
  }

  synchronizeURL(variant) {
    if (!variant || !this.productUrl) return;
    window.history.replaceState({ }, '', `${this.productUrl}?variant=${variant.id}`);
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;
    this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.querySelector('.product-form__error-message');
  }
  
  connectedCallback() {
    if (this.form) {
      this.onSubmitHandlerBound = this.onSubmitHandler.bind(this);
      this.form.addEventListener('submit', this.onSubmitHandlerBound);
    }
  }
  
  disconnectedCallback() {
    if (this.form && this.onSubmitHandlerBound) {
      this.form.removeEventListener('submit', this.onSubmitHandlerBound);
    }
  }

  async onSubmitHandler(evt) {
    evt.preventDefault();
    
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    
    if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;
    
    this.handleErrorMessage();
    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('is-loading');

    try {
      await CartAPI.addFromForm(this.form, ['cart-drawer', 'cart-icon-bubble']);
      
      const drawer = document.querySelector('es-drawer[id^="cart-drawer"]');
      if (drawer) {
        drawer.open();
      } else {
         // fallback if drawer is named differently
         const anyDrawer = document.querySelector('cart-drawer');
         if (anyDrawer) {
           const esDrawer = anyDrawer.closest('es-drawer');
           if (esDrawer) esDrawer.open();
         }
      }
    } catch (error) {
      let errorString = window.theme?.strings?.cartError || 'An error occurred';
      if (error.description) {
        if (typeof error.description === 'string') {
          errorString = error.description;
        } else if (typeof error.description === 'object') {
          errorString = Object.entries(error.description)
            .map(([key, value]) => {
              let cleanKey = key.replace(/properties\[(.*?)\]/g, '1').replace(/\[.*?\]/g, '').trim();
              return cleanKey ? cleanKey + ' ' + value : value;
            })
            .join(', ');
        }
      } else if (error.message) {
        errorString = error.message;
      }
      this.handleErrorMessage(errorString);
    } finally {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.classList.remove('is-loading');
    }
  }

  handleErrorMessage(errorMessage = false) {
    if (this.errorWrapper) {
      this.errorWrapper.hidden = !errorMessage;
      if (errorMessage && this.errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

